import type { Settings } from '../../shared/types/user'

const SETTINGS_KEY = 'settings'
const PENDING_SETTINGS_KEY = 'settings:pending'

/**
 * Removes cached settings from local storage.
 */
function clearSettingsCache(): void {
	localStorage.removeItem(SETTINGS_KEY)
}

/**
 * Removes pending settings from local storage.
 */
export function clearPendingSettingsCache(): void {
	localStorage.removeItem(PENDING_SETTINGS_KEY)
}

/**
 * Removes all settings snapshots from local storage.
 */
export function clearAllSettingsCache(): void {
	clearSettingsCache()
	clearPendingSettingsCache()
}

/**
 * Reads cached settings from local storage, if present.
 */
function getSettingsCache(): Settings | null {
	const cached = localStorage.getItem(SETTINGS_KEY)
	return cached ? (JSON.parse(cached) as Settings) : null
}

/**
 * Reads locally pending settings that have not been confirmed by the server.
 */
export function getPendingSettingsCache(): Settings | null {
	const cached = localStorage.getItem(PENDING_SETTINGS_KEY)
	return cached ? (JSON.parse(cached) as Settings) : null
}

/**
 * Returns the locally authoritative settings snapshot.
 */
export function getCurrentSettingsCache(): Settings | null {
	return getPendingSettingsCache() ?? getSettingsCache()
}

/**
 * Writes the latest settings snapshot to local storage.
 */
export function setSettingsCache(settings: Settings): void {
	localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
}

/**
 * Writes the latest pending settings snapshot to local storage.
 */
export function setPendingSettingsCache(settings: Settings): void {
	localStorage.setItem(PENDING_SETTINGS_KEY, JSON.stringify(settings))
}
